import { MembershipCard, User } from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMCard,
  AEMQuickLinks,
  Card,
  UserSession,
  formatNumericDate,
  getGenericLabel,
  getImage
} from '@com-delta-idp/mnx-skymiles-shared';
import { GenericLabel } from '@com-delta-idp/mnx-utils';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import { SkyClubMembership, TYPE } from '../models/overview.constants';

export const getMembershipCardDetails = (
  aem: AEMQuickLinks,
  profile: UserSession,
  tracker: StatusTrackerResponse | undefined,
  index: number
): MembershipCard => {
  return {
    image: getImage(
      aem.status[index].membershipcard.image.src || '',
      aem.status[index].membershipcard.image.alt || ''
    ),
    user: getUserData(aem, profile, tracker, index),
    barcode: getImage(
      aem.barcodeUrl + profile.smNumber,
      aem.status[index].membershipcard.altBarcodeText
    ),
    rules: aem.rules,
    info: aem.info
  };
};

const getUserData = (
  aem: AEMQuickLinks,
  profile: UserSession,
  tracker: StatusTrackerResponse | undefined,
  index: number
): User => {
  return {
    name: profile.firstName + ' ' + profile.lastName,
    skymilesNumber: profile.smNumber,
    validity: getValidityData(aem, getTierStatusExpireData(tracker), index)
  };
};

export const getTierStatusExpireData = (
  tracker: StatusTrackerResponse | undefined
): string | undefined => {
  const tierStatusExpireCheck = tracker?.loyaltyMemberTierLevels?.find(
    (tier) =>
      tier.tierStatusDesc === TYPE.CURRENT &&
      tier.tierLevelClassificatinCode === TYPE.TIER_CODE
  );
  return tierStatusExpireCheck
    ? tierStatusExpireCheck.tierLevelEndLocalDate
    : undefined;
};

const getValidityData = (
  aem: AEMQuickLinks,
  endDate: string | undefined,
  index: number
): GenericLabel => {
  let discontinuedDate = '';
  if (endDate) {
    const expiredDate = new Date(endDate + 'T12:00:00Z');

    discontinuedDate =
      expiredDate.toLocaleDateString('default', { month: 'short' }) +
      ' ' +
      expiredDate.toLocaleDateString('default', { day: 'numeric' }) +
      ' ' +
      expiredDate.toLocaleDateString('default', { year: 'numeric' });
  }

  return {
    title: aem.status[index].membershipcard.validThruLabel,
    subtitle: discontinuedDate.toUpperCase() || ''
  };
};

export const getSkyClubDescription = (
  aem: AEMQuickLinks,
  profile: UserSession
): string => {
  return profile.skyClubMemberType === SkyClubMembership.INDIVIDUAL_MEMBER
    ? aem.skyclub.individual
    : profile.skyClubMemberType === SkyClubMembership.EXECUTIVE_MEMBER
      ? aem.skyclub.executive
      : '';
};

export const getSkyClubMembershipCard = (
  quickLink: AEMCard,
  result: Card,
  profile: UserSession,
  aem: AEMQuickLinks
): Card => {
  if (
    quickLink.id === SkyClubMembership.SKY_CLUB_ID &&
    profile.isSkyClubMember === SkyClubMembership.IS_SKY_CLUB_MEMBER &&
    profile.membershipLevel !== SkyClubMembership.BASE_MEMBER
  ) {
    result.tag = getGenericLabel(
      getSkyClubDescription(aem, profile),
      `${aem.skyclub.expiry} ${formatNumericDate(
        profile.skyClubMembershipDiscontinueDate
      )}`
    );
  }
  return result;
};

export const getCardTitle = (
  aem: AEMQuickLinks,
  card: AEMCard,
  profile: UserSession
): string => {
  return card.id === SkyClubMembership.SKY_CLUB_ID &&
    profile.isSkyClubMember !== SkyClubMembership.IS_SKY_CLUB_MEMBER &&
    profile.membershipLevel !== SkyClubMembership.BASE_MEMBER
    ? aem.skyclub.nonMember
    : card.title;
};
